/**
 * The people, down the left.
 *
 * It does three jobs at once, which is why it is a rail and not a section:
 * it is the legend for every colour on the page, it is the summary of who did
 * the talking, and it is the filter — clicking a person narrows the transcript
 * to their turns and clicking again clears it.
 *
 * Talk share is drawn from `metrics.talk_pct` when the pipeline has produced
 * it, and falls back to the activity document's `speaking_ms` before then, so
 * the rail is useful in the window where the transcript exists but the metrics
 * have not landed. Which source is in use is stated, because two numbers
 * derived differently should never silently swap places under a reader.
 *
 * BOTH are shares of SPEECH, and the caption says so. `talk_pct` is defined
 * upstream as "share of all participants' speaking time, not share of call
 * duration"; the fallback divides one person's `speaking_ms` by the sum of
 * everyone's, which is the same denominator. Neither is a share of the call,
 * and the rail has no room to say how much of the call was silence — the
 * Insights tab draws that beside the same bars. See `insights-panel.tsx`.
 */

import { Crown } from 'lucide-react'
import type { ConversationMetrics } from '@era/shared'
import { Monogram } from './monogram'
import { formatDuration } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { SpeakerRoster } from '../speakers'

export function SpeakerRail({
  roster,
  metrics,
  speakingMs,
  focusIdentity,
  onFocus,
}: {
  roster: SpeakerRoster
  metrics: ConversationMetrics | null
  /** `identity → speaking_ms`, from the activity document. Used when metrics are absent. */
  speakingMs: Map<string, number>
  focusIdentity: string | null
  onFocus: (identity: string | null) => void
}) {
  const fallbackTotal = [...speakingMs.values()].reduce((sum, value) => sum + value, 0)
  const usingFallback = metrics === null && fallbackTotal > 0

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="eyebrow">
          {roster.list.length} {roster.list.length === 1 ? 'person' : 'people'}
        </p>
        {focusIdentity ? (
          <button
            type="button"
            onClick={() => onFocus(null)}
            className="text-[0.6875rem] font-medium text-accent hover:underline"
          >
            clear filter
          </button>
        ) : null}
      </div>

      <ul className="space-y-1">
        {roster.list.map((speaker) => {
          const pct = metrics
            ? (metrics.talk_pct[speaker.identity] ?? 0)
            : fallbackTotal > 0
              ? ((speakingMs.get(speaker.identity) ?? 0) / fallbackTotal) * 100
              : 0
          const seconds = metrics
            ? (metrics.talk_seconds[speaker.identity] ?? 0) * 1000
            : (speakingMs.get(speaker.identity) ?? 0)
          const known = metrics !== null || fallbackTotal > 0
          const selected = focusIdentity === speaker.identity

          return (
            <li key={speaker.identity}>
              <button
                type="button"
                onClick={() => onFocus(selected ? null : speaker.identity)}
                aria-pressed={selected}
                className={cn(
                  'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                  selected
                    ? 'border-accent-line bg-accent-soft/50'
                    : 'border-transparent hover:bg-surface-2',
                )}
              >
                <div className="flex items-center gap-2.5">
                  <Monogram
                    displayName={speaker.displayName}
                    index={speaker.index}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-[0.8125rem] font-medium text-ink">
                        {speaker.displayName}
                      </span>
                      {speaker.isCreator ? (
                        <Crown className="size-3 shrink-0 text-ink-dim" aria-label="created this call" />
                      ) : null}
                    </span>
                    {/* An em dash, not `0:00 · 0%`, when nothing has been
                        measured yet. Both people plainly spoke; printing zero
                        would be this screen asserting something it does not
                        know. */}
                    <span className="block truncate text-[0.6875rem] tabular text-ink-dim">
                      {known ? `${formatDuration(seconds)} · ${Math.round(pct)}%` : '—'}
                    </span>
                  </span>
                </div>

                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-sm bg-surface-2">
                  <span
                    className="block h-full"
                    style={{
                      width: `${Math.max(0, Math.min(100, pct))}%`,
                      backgroundColor: speaker.color,
                      borderRadius: '0 4px 4px 0',
                    }}
                  />
                </div>
              </button>
            </li>
          )
        })}
      </ul>

      <p className="px-3 text-[0.6875rem] leading-relaxed text-ink-dim">
        {metrics
          ? 'Share of speaking time, not of the call — these add up to 100%. Click a name to read only their turns.'
          : usingFallback
            ? 'Share of detected speech, from the activity document — not of the call. Final metrics arrive with the transcript.'
            : 'Talk share appears once the audio pipeline finishes.'}
      </p>
    </div>
  )
}
